import OrderUtils from 'utils/Order';
import basketUtils from 'utils/Basket';
import checkoutApi from 'services/api/checkout';
import checkoutUtils from 'utils/Checkout';
import LoadScripts from 'utils/LoadScripts';
import localeUtils from 'utils/LanguageLocale';
import store from 'Store';
import Actions from 'Actions';
import * as GooglePayHandlers from 'utils/GooglePayHandlers';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';
import locationUtils from 'utils/Location';

// Required by the official Google Pay spec
// (https://developers.google.com/pay/api/web/guides/tutorial)
const BASE_REQUEST = {
    apiVersion: 2,
    apiVersionMinor: 0
};

const ALLOWED_CARD_AUTH_METHODS = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];

const ALLOWED_CARD_NETWORKS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];

const GOOGLE_PAY_ERROR_MESSAGES = {
    incompleteCheckout: 'incompleteCheckout'
};

const SDK_LOAD_TIMEOUT_MS = 10000;

const TYPES = {
    ENABLED: 'ENABLED',
    HIDDEN: 'HIDDEN'
};

// Google Pay API enums — values are dictated by the SDK contract.
// See https://developers.google.com/pay/api/web/reference/request-objects.
// (Country codes 'US'/'CA' reuse localeUtils.COUNTRIES rather than duplicating here.)
const PAYMENT_METHOD_TYPES = {
    CARD: 'CARD'
};

const TOKENIZATION_TYPES = {
    PAYMENT_GATEWAY: 'PAYMENT_GATEWAY'
};

const ADDRESS_FORMATS = {
    FULL: 'FULL'
};

const TOTAL_PRICE_STATUS = {
    ESTIMATED: 'ESTIMATED',
    FINAL: 'FINAL'
};

const CHECKOUT_OPTIONS = {
    COMPLETE_IMMEDIATE_PURCHASE: 'COMPLETE_IMMEDIATE_PURCHASE'
};

const CALLBACK_INTENTS = {
    PAYMENT_AUTHORIZATION: 'PAYMENT_AUTHORIZATION',
    SHIPPING_ADDRESS: 'SHIPPING_ADDRESS',
    SHIPPING_OPTION: 'SHIPPING_OPTION'
};

const SDK_STATUS_CODES = {
    CANCELED: 'CANCELED'
};

// Keys into utils/locales/GooglePay_{en_US,fr_CA}.js
const LOCALE_KEYS = {
    ESTIMATED_TOTAL: 'estimatedTotal',
    TOTAL: 'total'
};

// Holds data for the current checkout (order, shippingGroup, shippingMethods, etc) so future callbacks
// don't have to refetch it
let checkoutContext = null;

/**
 * The two-letter country codes Google Pay should accept for the shipping address.
 *
 * @returns {string[]}
 */
function getAllowedCountryCodes() {
    return [localeUtils.isCanada() ? localeUtils.COUNTRIES.CA : localeUtils.COUNTRIES.US];
}

/**
 * Maps the Cart Service available shipping methods to Google Pay's
 * `shippingOptionParameters`. Google's option has no amount field, so the fee
 * is appended to the description
 *
 * @param {Array} shippingMethods - from checkoutApi.getAvailableShippingMethods
 * @param {string|number} [currentShippingMethodId] - the order's current method, used as default
 * @returns {{ defaultSelectedOptionId: string, shippingOptions: Array<{id,label,description}> }}
 */
function getShippingOptions(shippingMethods = [], currentShippingMethodId) {
    const shippingOptions = shippingMethods.map(method => ({
        id: String(method.shippingMethodId),
        label: method.shippingMethodType,
        description: [method.shippingMethodDescription, checkoutUtils.setShippingFee(method.shippingFee)].filter(Boolean).join(' — ')
    }));

    const currentId = String(currentShippingMethodId);
    const defaultSelectedOptionId = shippingOptions.some(option => option.id === currentId) ? currentId : shippingOptions[0]?.id;

    return { defaultSelectedOptionId, shippingOptions };
}

function getBaseCardPaymentMethod() {
    return {
        type: PAYMENT_METHOD_TYPES.CARD,
        parameters: {
            allowedAuthMethods: ALLOWED_CARD_AUTH_METHODS,
            allowedCardNetworks: ALLOWED_CARD_NETWORKS
        }
    };
}

function getTokenizationSpecification() {
    const { googlePayGateway, gatewayMerchantIdUS, gatewayMerchantIdCA } = Sephora.configurationSettings.googlePayConfig || {};
    const gatewayMerchantId = localeUtils.isCanada() ? gatewayMerchantIdCA : gatewayMerchantIdUS;

    return {
        type: TOKENIZATION_TYPES.PAYMENT_GATEWAY,
        parameters: {
            gateway: googlePayGateway,
            gatewayMerchantId
        }
    };
}

function getCardPaymentMethod() {
    const baseCardPaymentMethod = getBaseCardPaymentMethod();

    return {
        ...baseCardPaymentMethod,
        parameters: {
            ...baseCardPaymentMethod.parameters,
            // Returns the billing address tied to the selected card
            billingAddressRequired: true,
            billingAddressParameters: {
                format: ADDRESS_FORMATS.FULL,
                phoneNumberRequired: true
            }
        },
        tokenizationSpecification: getTokenizationSpecification()
    };
}

function getIsReadyToPayRequest() {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getBaseCardPaymentMethod()]
    };
}

function getMerchantInfo() {
    const { merchantId, merchantName } = Sephora.configurationSettings.googlePayConfig || {};

    return {
        merchantId,
        merchantName
    };
}

function getTransactionInfo({ transactionAmount, transactionCurrencyCode }) {
    // Resolve EN/FR at call time (not module load) so the label follows the
    // customer's current locale — same pattern as utils/Checkout setShippingFee.
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return {
        countryCode: localeUtils.isCanada() ? localeUtils.COUNTRIES.CA : localeUtils.COUNTRIES.US,
        currencyCode: transactionCurrencyCode,
        totalPriceStatus: TOTAL_PRICE_STATUS.ESTIMATED,
        totalPrice: String(transactionAmount),
        totalPriceLabel: getText(LOCALE_KEYS.ESTIMATED_TOTAL)
    };
}

function getSthPaymentDataRequest({ transactionAmount, transactionCurrencyCode, shippingMethods, currentShippingMethodId }) {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        shippingAddressRequired: true,
        shippingAddressParameters: {
            phoneNumberRequired: true,
            allowedCountryCodes: getAllowedCountryCodes()
        },
        shippingOptionRequired: true,
        shippingOptionParameters: getShippingOptions(shippingMethods, currentShippingMethodId),
        callbackIntents: [CALLBACK_INTENTS.SHIPPING_ADDRESS, CALLBACK_INTENTS.SHIPPING_OPTION, CALLBACK_INTENTS.PAYMENT_AUTHORIZATION]
    };
}

function getBopisPaymentDataRequest({ transactionAmount, transactionCurrencyCode }) {
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: {
            ...getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
            // FINAL is required when checkoutOption is COMPLETE_IMMEDIATE_PURCHASE.
            // BOPIS has no shipping cost and tax is computed on the pickup-store ZIP,
            // so the basket subtotal is the final amount the customer will be charged.
            totalPriceStatus: TOTAL_PRICE_STATUS.FINAL,
            // Override the inherited "Estimated Total" label — BOPIS price is final,
            // so an "estimated" label would mislead the customer.
            totalPriceLabel: getText(LOCALE_KEYS.TOTAL),
            checkoutOption: CHECKOUT_OPTIONS.COMPLETE_IMMEDIATE_PURCHASE
        },
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        // BOPIS skips shipping fields: pickup is preset on the basket, so the
        // paysheet does not ask for a shipping address or method.
        callbackIntents: [CALLBACK_INTENTS.PAYMENT_AUTHORIZATION]
    };
}

function getSddPaymentDataRequest({ transactionAmount, transactionCurrencyCode }) {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        shippingAddressRequired: true,
        shippingAddressParameters: {
            phoneNumberRequired: true,
            allowedCountryCodes: getAllowedCountryCodes()
        },
        // SDD / Mixed paysheet has no shipping-method selector
        shippingOptionRequired: false,
        callbackIntents: [CALLBACK_INTENTS.SHIPPING_ADDRESS, CALLBACK_INTENTS.PAYMENT_AUTHORIZATION]
    };
}

/**
 * Constructs a Google Pay PaymentsClient with the given callbacks. Centralizes the
 * SDK availability check and environment lookup shared across all three cart-type
 * clients (STH / BOPIS / SDD).
 *
 * @param {Object} paymentDataCallbacks - Google Pay paymentDataCallbacks dict
 * @returns {google.payments.api.PaymentsClient}
 * @throws {Error} when the SDK script has not been loaded yet.
 */
function createPaymentsClient(paymentDataCallbacks) {
    const PaymentsClient = window.google?.payments?.api?.PaymentsClient;

    if (!PaymentsClient) {
        throw new Error('Google Pay SDK not loaded');
    }

    const { googlePayEnvironment } = Sephora.configurationSettings.googlePayConfig || {};

    return new PaymentsClient({
        environment: googlePayEnvironment,
        paymentDataCallbacks
    });
}

const GooglePayUtils = {
    TYPES,
    GOOGLE_PAY_ERROR_MESSAGES,

    paymentsClient: null,

    /**
     * Whether Google Pay should be offered to the current user, based on the
     * locale-specific killswitches in `googlePayConfig`.
     *
     * @returns {boolean}
     */
    isGooglePayEnabled: () => {
        const { isGooglePayDotComEnabledUS, isGooglePayDotComEnabledCA } = Sephora.configurationSettings.googlePayConfig || {};

        if (localeUtils.isUS()) {
            return Boolean(isGooglePayDotComEnabledUS?.isEnabled);
        } else if (localeUtils.isCanada()) {
            return Boolean(isGooglePayDotComEnabledCA?.isEnabled);
        }

        return false;
    },

    /**
     * Returns Google Pay cart eligibility from Redux basket state via basketUtils;
     * checkout returns `false` until the order-payload helper lands (see TODO).
     *
     * @returns {boolean}
     */
    isGooglePayCartEligibleForCurrentPage: function () {
        const isCheckout = locationUtils.isCheckout();

        if (isCheckout) {
            return OrderUtils.isGooglePayEnabledForThisOrder();
        }

        return basketUtils.isGooglePayCartEligible();
    },
    /**
     * Resolves the Google Pay button visibility state for the basket surface.
     * Returns HIDDEN when any gate fails (cheap → expensive): locale killswitch,
     * payment eligibility, basket surface enable, cart eligibility, or the SDK
     * `isReadyToPay` probe. Errors during SDK load or the probe are treated as HIDDEN.
     *
     * Basket-only by name; checkout needs its own helper once the order-payload
     * eligibility check lands (TODO inside isGooglePayCartEligibleForCurrentPage).
     *
     * @returns {Promise<'ENABLED' | 'HIDDEN'>}
     */
    getGooglePaymentTypeForBasket: async function () {
        if (!this.isGooglePayEnabled()) {
            return TYPES.HIDDEN;
        }

        const { googlePayConfig } = Sephora.configurationSettings;
        const isBasketSurfaceEnabled = Boolean(googlePayConfig?.isGooglePayEnabledBasket?.isEnabled);
        const isCartEligible = this.isGooglePayCartEligibleForCurrentPage();

        if (!isBasketSurfaceEnabled || !isCartEligible) {
            return TYPES.HIDDEN;
        }

        try {
            await this.initPaymentsClient();

            return TYPES.ENABLED;
        } catch (err) {
            return TYPES.HIDDEN;
        }
    },

    /**
     * Singleton for google.payments.api.PaymentsClient. Created on first call;
     * subsequent calls return the same instance.
     *
     * environment: 'TEST' | 'PRODUCTION' (official SDK values).
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getPaymentsClient: function () {
        if (!this.paymentsClient) {
            this.paymentsClient = createPaymentsClient({
                onPaymentDataChanged: data => this.onPaymentDataChanged(data),
                onPaymentAuthorized: paymentData => this.onSthPaymentAuthorized(paymentData)
            });
        }

        return this.paymentsClient;
    },

    /**
     * Singleton for the BOPIS-only PaymentsClient. Attaches `onPaymentAuthorized` to
     * the SDK constructor because the BOPIS PaymentDataRequest sets
     * `callbackIntents: ['PAYMENT_AUTHORIZATION']`, and Google Pay requires the
     * matching callback to be present on the client.
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getBopisPaymentsClient: function () {
        if (!this.bopisPaymentsClient) {
            this.bopisPaymentsClient = createPaymentsClient({
                onPaymentAuthorized: paymentData => this.onBopisPaymentAuthorized(paymentData)
            });
        }

        return this.bopisPaymentsClient;
    },

    /**
     * Singleton for the SDD / Mixed PaymentsClient.
     *
     * Kept separate from `getPaymentsClient` (STH) so attaching
     * `onPaymentAuthorized` here does not force STH requests to declare
     * PAYMENT_AUTHORIZATION.
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getSddPaymentsClient: function () {
        if (!this.sddPaymentsClient) {
            this.sddPaymentsClient = createPaymentsClient({
                onPaymentDataChanged: data => this.onPaymentDataChanged(data),
                onPaymentAuthorized: paymentData => this.onSddPaymentAuthorized(paymentData)
            });
        }

        return this.sddPaymentsClient;
    },

    /**
     * Creates the Google Pay payment group on the backend (mirrors Paze's
     * initPaymentGroup) and resolves with the refreshed order details.
     *
     * @param {string|number} orderId - the checkout order id resolved from
     *   initializeCheckout
     * @param {Object} [options]
     * @param {boolean} [options.isBopis] - when true, sends the RopisCheckout property
     * @returns {Promise<Object>} resolved order details
     */
    initGooglePaymentGroup: async function (orderId, { isBopis = false } = {}) {
        await checkoutApi.initializePaymentGroup({
            paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.GOOGLE_PAY,
            // PascalCase `RopisCheckout` is what the cart-service BE expects on this
            // endpoint. The companion `initializeCheckout({ ropisCheckout })` call uses camelCase
            // because that helper transforms it to PascalCase internally (Checkout.js:392).
            ...(isBopis && { RopisCheckout: true })
        });

        return checkoutApi.getOrderDetails(orderId);
    },

    /**
     * Called after the Google Pay SDK script has loaded. Instantiates the
     * PaymentsClient and calls `isReadyToPay` to confirm the device/browser
     * supports Google Pay.
     *
     * @returns {Promise<void>} resolves when Google Pay is confirmed available
     * @throws {Error} if `isReadyToPay` returns a falsy result, indicating
     *   Google Pay is not supported on this device
     */
    onLibraryLoad: async function () {
        const client = this.getPaymentsClient();
        const response = await client.isReadyToPay(getIsReadyToPayRequest());

        if (!response?.result) {
            // TODO: GOOGLE — handle Google Pay unavailable on user's machine
            throw new Error('Google Pay is not available on this device');
        }
    },

    /**
     * Loads the SDK and verifies Google Pay is available on this device.
     * Memoizes the in-flight Promise so concurrent callers (e.g. desktop +
     * mobile ButtonGrid instances) share a single SDK load. On rejection
     * the cache is cleared so it can be retried.
     *
     * @returns {Promise<void>} resolves when the client is ready for
     *   startCheckout; rejects if the SDK can't load or the device is ineligible.
     */
    initPaymentsClient: function () {
        if (!this.initPromise) {
            this.initPromise = (async () => {
                try {
                    await this.loadLibrary();
                    await this.onLibraryLoad();
                } catch (err) {
                    this.initPromise = null;
                    this.loadLibraryPromise = null;
                    this.paymentsClient = null;
                    throw err;
                }
            })();
        }

        return this.initPromise;
    },

    /**
     * Loads the Google Pay JS SDK once (memoized). Rejects if the library
     * URL is missing from config or the script fails to load within
     * SDK_LOAD_TIMEOUT_MS — without these guards the Promise could hang
     * forever (LoadScripts has no onerror handler).
     *
     * @returns {Promise<void>} resolves when the script is loaded
     */
    loadLibrary: function () {
        if (!this.loadLibraryPromise) {
            const url = Sephora.configurationSettings.googlePayConfig?.googlePayLibrary;

            if (!url) {
                this.loadLibraryPromise = Promise.reject(new Error('Google Pay library URL not configured'));
            } else {
                this.loadLibraryPromise = new Promise((resolve, reject) => {
                    const timeoutId = setTimeout(() => reject(new Error('Google Pay SDK load timed out')), SDK_LOAD_TIMEOUT_MS);

                    LoadScripts.loadScripts(
                        [url],
                        () => {
                            clearTimeout(timeoutId);
                            resolve();
                        },
                        true
                    );
                });
            }
        }

        return this.loadLibraryPromise;
    },

    /**
     * Shared STH / SDD / Mixed shipping callback. Delegates the mid-sheet
     * SHIPPING_ADDRESS, SHIPPING_OPTION (STH only), and INITIALIZE triggers to
     * the shared dispatcher in GooglePayHandlers. The dispatcher branches on
     * `hasShippingOptions` (read from checkoutContext): strict `=== false`
     * takes the SDD/Mix path (recalc only); anything else, including
     * `true` (STH happy path) and `undefined` (no startCheckout has populated
     * checkoutContext yet), takes the STH path (recalc + shipping methods).
     *
     * @param {Object} intermediatePaymentData
     * @returns {Promise<Object>} PaymentDataRequestUpdate or PaymentDataError
     */
    onPaymentDataChanged: async function (intermediatePaymentData) {
        return GooglePayHandlers.onPaymentDataChanged(intermediatePaymentData, {
            orderId: checkoutContext?.orderId,
            shippingGroupId: checkoutContext?.shippingGroupId,
            hasShippingOptions: checkoutContext?.hasShippingOptions,
            transactionCurrencyCode: checkoutContext?.transactionCurrencyCode
        });
    },

    /**
     * STH authorization callback. Delegates to the shared handler which submits
     * the PaymentData to Cart Service and returns the SDK's
     * PaymentAuthorizationResult. The handler also stashes the placeOrder
     * response on `checkoutContext` so the post-paysheet redirect can read it
     * from `startCheckout`'s return value.
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string, error?: Object }>}
     */
    onSthPaymentAuthorized: async function (paymentData) {
        return GooglePayHandlers.onPaymentAuthorized(paymentData, checkoutContext);
    },

    /**
     * BOPIS paysheet authorization callback. Delegates to the shared
     * handler. The BOPIS `checkoutContext` omits `shippingGroupId`
     * and the paysheet omits `paymentData.shippingAddress`, so the shared
     * handler's address AND-guard skips the shipping-address call naturally;
     * the attach + placeOrder({ isExpressCheckout: true }) sequence still runs.
     * The pickup store is preserved because `initializeCheckout({ ropisCheckout: true })`
     * was passed at BOPIS init time — the backend keeps the pickup configuration
     * through submit.
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string, error?: Object }>}
     */
    onBopisPaymentAuthorized: async function (paymentData) {
        return GooglePayHandlers.onPaymentAuthorized(paymentData, checkoutContext);
    },

    /**
     * SDD / Mixed paysheet authorization callback. Delegates to the shared
     * handler. The SDD `checkoutContext` populates `shippingAddress` (from the
     * paysheet) plus `shippingGroupIds` + `shippingMethodIdsByGroup`,
     * so the shared handler's fan-outs attach the address and
     * method to every physical shipping group in a Mixed basket before the
     * attach + placeOrder({ isExpressCheckout: true }) sequence runs.
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string, error?: Object }>}
     */
    onSddPaymentAuthorized: async function (paymentData) {
        return GooglePayHandlers.onPaymentAuthorized(paymentData, checkoutContext);
    },

    /**
     * Opens the Google Pay payment sheet with a prebuilt PaymentDataRequest and maps
     * the user-cancel outcome. Must be invoked AFTER `await initPaymentsClient()`.
     *
     * @param {Object} paymentDataRequest
     * @returns {Promise<{ googlePay: { paymentData: Object } }>}
     * @throws {Error} with `reason === 'incompleteCheckout'` when the user cancels.
     */
    requestPaymentData: async function (paymentDataRequest, googleClient) {
        const client = googleClient || this.getPaymentsClient();

        try {
            const paymentData = await client.loadPaymentData(paymentDataRequest);

            return { googlePay: { paymentData } };
        } catch (err) {
            // Google Pay returns this statusCode when the user closes the sheet
            if (err?.statusCode === SDK_STATUS_CODES.CANCELED) {
                const error = new Error('User cancelled Google Pay checkout');

                error.reason = GOOGLE_PAY_ERROR_MESSAGES.incompleteCheckout;

                throw error;
            }

            throw err;
        }
    },

    /**
     * Runs the express-checkout init sequence, then opens the paysheet.
     *
     * STH:   order/init → paymentGroup (GooglePayPaymentGroup) → getOrderDetails →
     *        getAvailableShippingMethods → STH PaymentDataRequest → loadPaymentData.
     * BOPIS: order/init (RopisCheckout) → paymentGroup (GooglePayPaymentGroup,
     *        RopisCheckout) → getOrderDetails → BOPIS PaymentDataRequest →
     *        loadPaymentData. Skips the shipping-methods lookup (no shipping for
     *        pickup).
     * SDD:   order/init → paymentGroup (GooglePayPaymentGroup) → getOrderDetails →
     *        SDD PaymentDataRequest → loadPaymentData. Skips the shipping-methods
     *        lookup (paysheet has no method selector for SDD/Mix; the cart's
     *        fulfillment composition fixes delivery). Used for SDD-only AND
     *        Mixed (STH+SDD) baskets.
     *
     * Populates `checkoutContext`, reserved for callback bodies.
     * On any init failure the order-failure flow runs and the sheet is NOT opened.
     *
     * @param {Object} args
     * @param {string} args.transactionAmount - amount in major units (e.g. '10.00')
     * @param {string} args.transactionCurrencyCode - ISO 4217 code (e.g. 'USD', 'CAD')
     * @param {boolean} [args.isBopis] - true when the basket is a BOPIS pickup cart
     * @param {boolean} [args.isSdd] - true when the basket has SDD items (SDD-only or
     *   Mixed STH+SDD). Ignored when `isBopis` is true.
     * @returns {Promise<{ googlePay: { paymentData: Object }, orderResponse?: Object } | undefined>}
     *   On success, `orderResponse` carries the placeOrder result stashed on
     *   checkoutContext by the shared onPaymentAuthorized handler — the caller
     *   passes it to `checkoutUtils.placeOrderSuccess` for the confirmation redirect.
     * @throws {Error} with `reason === 'incompleteCheckout'` when the user cancels.
     */
    startCheckout: async function ({ transactionAmount, transactionCurrencyCode, isBopis = false, isSdd = false }) {
        checkoutContext = null;

        let orderDetails;
        let shippingMethods = [];

        if (isBopis) {
            store.dispatch(Actions.showInterstice(true));
        }

        let googleClient;

        try {
            if (isBopis) {
                googleClient = this.getBopisPaymentsClient();
            } else if (isSdd) {
                googleClient = this.getSddPaymentsClient();
            } else {
                googleClient = this.getPaymentsClient();
            }

            // initializeCheckout resolves with the checkout order; pass it on.
            // On the basket page OrderUtils.getOrderId() (order.orderDetails store slice)
            // is still empty, so reading it here would call getOrderDetails(undefined).
            const order = await checkoutUtils.initializeCheckout({ isGooglePayFlow: true, ropisCheckout: isBopis });
            const orderId = order?.orderId || order?.currentOrderId;

            orderDetails = await this.initGooglePaymentGroup(orderId, { isBopis });

            if (isBopis) {
                // BOPIS skips the shipping-group lookup and the shipping-methods call —
                // pickup is preset on the basket so the paysheet collects no shipping data.
                checkoutContext = {
                    orderId,
                    priceInfo: orderDetails?.priceInfo,
                    isBopis: true
                };
            } else if (isSdd) {
                // SDD / Mixed: resolve the SAME_DAY shipping group; skip the
                // shipping-methods fetch (paysheet has no method selector).
                const shippingGroup = OrderUtils.getSameDayShippingGroup(orderDetails);

                if (!shippingGroup) {
                    throw new Error('Unsupported cart type: missing SameDayShippingGroup');
                }

                checkoutContext = {
                    orderId,
                    shippingGroupId: shippingGroup.shippingGroupId,
                    shippingGroupIds: OrderUtils.getPhysicalAddressShippingGroupIds(orderDetails),
                    shippingMethodIdsByGroup: OrderUtils.getShippingMethodIdsByPhysicalGroup(orderDetails),
                    priceInfo: orderDetails?.priceInfo,
                    transactionCurrencyCode,
                    hasShippingOptions: false,
                    isSdd: true
                };
            } else {
                const shippingGroup = OrderUtils.getHardGoodShippingGroup(orderDetails);

                if (!shippingGroup) {
                    throw new Error('Unsupported cart type: missing HardGoodShippingGroup');
                }

                const shippingGroupId = shippingGroup.shippingGroupId;
                const currentShippingMethodId = shippingGroup.shippingMethod?.shippingMethodId;

                const response = await checkoutApi.getAvailableShippingMethods(orderId, shippingGroupId);
                shippingMethods = response?.shippingMethods || [];

                checkoutContext = {
                    orderId,
                    shippingGroupId,
                    shippingGroupIds: OrderUtils.getPhysicalAddressShippingGroupIds(orderDetails),
                    shippingMethodIdsByGroup: OrderUtils.getShippingMethodIdsByPhysicalGroup(orderDetails),
                    shippingMethods,
                    priceInfo: orderDetails?.priceInfo,
                    currentShippingMethodId,
                    transactionCurrencyCode,
                    hasShippingOptions: true,
                    isBopis: false
                };
            }
        } catch (err) {
            Sephora.logger.error('[GooglePay] checkout init failed', err);
            checkoutUtils.initOrderFailure(err);

            if (isBopis) {
                store.dispatch(Actions.showInterstice(false));
            }

            return undefined;
        }

        let paymentDataRequest;

        if (isBopis) {
            paymentDataRequest = getBopisPaymentDataRequest({ transactionAmount, transactionCurrencyCode });
        } else if (isSdd) {
            paymentDataRequest = getSddPaymentDataRequest({ transactionAmount, transactionCurrencyCode });
        } else {
            paymentDataRequest = getSthPaymentDataRequest({
                transactionAmount,
                transactionCurrencyCode,
                shippingMethods,
                currentShippingMethodId: checkoutContext.currentShippingMethodId
            });
        }

        try {
            const paysheetResult = await this.requestPaymentData(paymentDataRequest, googleClient);

            return {
                ...paysheetResult,
                orderResponse: checkoutContext?.orderResponse
            };
        } finally {
            if (isBopis) {
                store.dispatch(Actions.showInterstice(false));
            }
        }
    }
};

export default GooglePayUtils;
